import type { FC } from "react"
import { useForm } from "react-hook-form"

import type { ProductInventoriesSearchParams } from "../../schemas"

import { SearchFilterDrawer } from "@/components/common/SearchFilterDrawer/SearchFilterDrawer"
import type { ComputedProductStatus, InventoryStatus } from "@/graphql/codegen/graphql"
import { InventoryFilterList, InventoryFilterSearchableList } from "@/screens/Inventory/components"
import type { Option } from "@/types/form-fields"

interface ProductInventoryFilterDrawerProps {
  open: boolean
  searchParams: ProductInventoriesSearchParams
  productStatusOptions: Option<ComputedProductStatus>[]
  productInventoryStatusOptions: Option<InventoryStatus>[]
  productManagerOptions: Option<string>[]
  productClassOptions: Option<string>[]
  onToggle: () => void
  onChange: <K extends keyof ProductInventoriesSearchParams>(key: K, value: ProductInventoriesSearchParams[K]) => void
  onClear: () => void
}

export const ProductInventoryFilterDrawer: FC<ProductInventoryFilterDrawerProps> = ({
  open,
  searchParams,
  productStatusOptions,
  productInventoryStatusOptions,
  productManagerOptions,
  productClassOptions,
  onToggle,
  onChange,
  onClear,
}) => {
  const productStatuses = searchParams.productStatuses ?? []
  const inventoryStatuses = searchParams.inventoryStatuses ?? []
  const productManagerIds = searchParams.productManagerIds ?? []
  const productClassIds = searchParams.productClassIds ?? []

  const { control } = useForm({
    defaultValues: { productStatuses, inventoryStatuses, productManagerIds, productClassIds },
  })

  return (
    <SearchFilterDrawer open={open} toggleDrawer={onToggle} clear={onClear} testId='product-inventory.filter-drawer'>
      <div className='flex h-full flex-col gap-y-4 overflow-y-auto pb-4'>
        <InventoryFilterList
          label='Product Status'
          control={control}
          name='productStatuses'
          values={productStatuses}
          options={productStatusOptions}
          onChange={(values) => onChange("productStatuses", values)}
          testId='product-inventory.product-status-filter'
        />

        <InventoryFilterList
          label='Inventory Status'
          control={control}
          name='inventoryStatuses'
          values={inventoryStatuses}
          options={productInventoryStatusOptions}
          onChange={(values) => onChange("inventoryStatuses", values)}
          testId='product-inventory.inventory-status-filter'
        />

        <InventoryFilterSearchableList
          label='Product Manager'
          control={control}
          name='productManagerIds'
          values={productManagerIds}
          options={productManagerOptions}
          onChange={(values) => onChange("productManagerIds", values)}
          testId='product-inventory.product-manager-filter'
        />

        <InventoryFilterSearchableList
          label='Category/Class'
          control={control}
          name='productClassIds'
          values={productClassIds}
          options={productClassOptions}
          onChange={(values) => onChange("productClassIds", values)}
          testId='product-inventory.product-class-filter'
        />
      </div>
    </SearchFilterDrawer>
  )
}
